'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Clapperboard } from 'lucide-react';
import { MARKETING_TOKENS as T } from './marketingTokens';
import MarketingPill from './MarketingPill';
import VideoEmbed from './VideoEmbed';
import { MARKETING_MODULES } from '@/lib/marketingModules';
import {
  HOW_IT_WORKS_CHAPTERS,
  YOUTUBE_CHANNEL_URL,
  type Chapter,
} from '@/lib/howItWorksVideos';
import { demoHref } from '@/lib/contactHref';

/**
 * /how-it-works body.
 *
 * A story about a project told in moving pictures: eight chapters following a
 * job from the enquiry to the money, each one a short setup followed by its
 * recordings of the real screens.
 *
 * ONE LAYOUT RULE, and it has no exceptions:
 *
 *   One large 16:9 recording per row, at every screen size. No featured or
 *   supporting sizes, no mini cards, no two- or three-across grids, and no text
 *   column beside the video stealing its width.
 *
 * The reason is legibility, not taste: these recordings show real application
 * screens — a muster, a bill, a payment plan — and at half the content width
 * none of that is readable, which defeats the point of showing them at all.
 * Every earlier cut that made a recording smaller, whether by ranking it as
 * "supporting" or by putting two in a row, produced a video gallery rather than
 * a product walkthrough.
 *
 * The intro copy and the recordings have DIFFERENT widths on purpose: prose
 * wants a ~680px measure to stay readable, the recordings want every pixel they
 * can get. The video list must never inherit the intro's max-width.
 *
 * The page is long because twenty-six large recordings are long. That is the
 * intended trade — a visitor who scrolls it can actually learn the product.
 */

const SERIF = 'var(--font-serif), Georgia, "Times New Roman", serif';
const MONO = 'var(--font-mono), ui-monospace, monospace';
/** min(100% - gutter, 1240px) — see HIW_CSS. */
const CONTAINER = 'hiw-container';
const SECTION_PY = 'py-[72px] md:py-[100px] lg:py-[120px]';

const HIW_CSS = `
.hiw-container{width:min(100% - 32px, 1240px);margin-inline:auto}
@media (min-width:768px){.hiw-container{width:min(100% - 40px, 1240px)}}
.hiw-chapter{scroll-margin-top:118px}
.hiw-cta{transition:background .2s ease,box-shadow .2s ease;background:var(--mk-primary);box-shadow:0 10px 26px rgba(0,0,0,0.18)}
.hiw-cta:hover{background:var(--mk-primary-dark);box-shadow:0 14px 32px rgba(0,0,0,0.24)}
.hiw-cta .l-arrow{transition:transform .2s ease}
.hiw-cta:hover .l-arrow{transform:translateX(3px)}
.hiw-play-mark{transition:transform .2s ease}
.hiw-play:hover .hiw-play-mark{transform:translate(-50%,-50%) scale(1.06)}
.hiw-play:focus-visible{outline:3px solid var(--mk-primary);outline-offset:3px}
/* Workflow rail — a thin line of text, scrolled sideways on a phone. */
.hiw-rail{position:sticky;top:73px;z-index:5}
.hiw-rail-track{overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none}
.hiw-rail-track::-webkit-scrollbar{display:none}
.hiw-rail a{transition:color .18s ease;white-space:nowrap}
.hiw-rail a:hover{color:var(--mk-primary-dark)}
/* The video list. One per row, always. The max-width is the RECORDING's, not
   the prose's — the intro above it is capped far narrower. */
.hiw-videos{display:flex;flex-direction:column;gap:56px;width:100%;max-width:1120px}
@media (min-width:768px){.hiw-videos{gap:72px}}
`;

const moduleLabel = (slug: string) => MARKETING_MODULES.find((m) => m.slug === slug)?.label ?? slug;

/** Band colours for a chapter's tone. Bands, not cards. */
const band = (tone: Chapter['tone']) => {
  if (tone === 'dark') {
    return {
      bg: T.TEXT,
      heading: '#FFFFFF',
      body: 'rgba(255,255,255,0.76)',
      soft: 'rgba(255,255,255,0.64)',
      rule: 'rgba(255,255,255,0.16)',
      mark: T.ACCENT_LIGHT,
    };
  }
  return {
    bg: tone === 'alt' ? T.SURFACE_ALT : T.BG,
    heading: T.TEXT,
    body: T.TEXT_MUTED,
    soft: T.TEXT_SOFT,
    rule: T.BORDER,
    mark: T.PRIMARY,
  };
};

function ChapterSection({ c }: { c: Chapter }) {
  const colors = band(c.tone);
  const onDark = c.tone === 'dark';

  return (
    <section
      id={c.slug}
      className={`hiw-chapter ${SECTION_PY}`}
      style={{ backgroundColor: colors.bg, borderTop: `1px solid ${colors.rule}` }}
    >
      <div className={CONTAINER}>
        {/* Setup: above the recordings, never beside them. */}
        {/* Prose measure — deliberately narrower than the recordings below. */}
        <div className="max-w-[680px]">
          <div className="text-[13px] font-bold tracking-[0.18em]" style={{ fontFamily: MONO, color: colors.mark }}>
            {c.n}
          </div>
          <h2
            className="mt-3 text-[32px] md:text-[44px] lg:text-[48px]"
            style={{ fontFamily: SERIF, fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.08, color: colors.heading }}
          >
            {c.label}
          </h2>
          <p className="mt-5 max-w-[60ch] text-[16px] leading-[1.7] md:text-[18px]" style={{ color: colors.body }}>
            {c.lede}
          </p>
          <p className="mt-4 max-w-[60ch] text-[15px] leading-[1.65] md:text-[16px]" style={{ color: colors.heading }}>
            <span className="font-semibold" style={{ color: colors.mark }}>What you end up with — </span>
            {c.outcome}
          </p>
          {/* Which modules do the work here. Labels, not links — the per-module
              landing pages were removed and /modules is one click away. */}
          <div className="mt-6 flex flex-wrap items-center gap-x-2.5 gap-y-2">
            {c.modules.map((slug, i) => (
              <span key={slug} className="flex items-center gap-2.5">
                {i > 0 && <span aria-hidden style={{ color: colors.rule }}>·</span>}
                <span className="text-[12.5px] font-bold uppercase tracking-[0.11em]" style={{ fontFamily: MONO, color: colors.soft }}>
                  {moduleLabel(slug)}
                </span>
              </span>
            ))}
          </div>
        </div>

        <div className="hiw-videos mt-12 md:mt-16">
          {c.videos.map((v) => (
            /* No outer card: the frame, then the words. A panel around each one
               would turn the page back into a gallery of tiles. */
            <figure key={v.id} className="m-0">
              <VideoEmbed id={v.id} title={v.title} onDark={onDark} />
              <div
                className="pt-5 text-[18px] font-semibold md:text-[21px]"
                style={{ color: colors.heading, fontFamily: SERIF, letterSpacing: '-0.015em' }}
              >
                {v.title}
              </div>
              <p className="mt-2 max-w-[62ch] text-[15px] leading-[1.65] md:text-[16px]" style={{ color: colors.soft }}>
                {v.blurb}
              </p>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * The workflow rail: the spine of the page and its only secondary navigation.
 * A thin line of text — no pills, no shadows — with a dot marking where you
 * currently are, tracked by an observer rather than by the URL hash so it stays
 * right when you scroll past a chapter without clicking anything.
 */
function WorkflowRail() {
  const [active, setActive] = useState(HOW_IT_WORKS_CHAPTERS[0].slug);
  const trackRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const seen = new Map<string, number>();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => seen.set(e.target.id, e.intersectionRatio));
        const best = [...seen.entries()].sort((a, b) => b[1] - a[1])[0];
        if (best && best[1] > 0) setActive(best[0]);
      },
      { rootMargin: '-118px 0px -55% 0px', threshold: [0, 0.25, 0.5, 1] },
    );
    HOW_IT_WORKS_CHAPTERS.forEach((c) => {
      const el = document.getElementById(c.slug);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  // Keep the active step in view on a phone, where the rail scrolls sideways.
  useEffect(() => {
    const track = trackRef.current;
    const el = track?.querySelector<HTMLElement>(`[data-slug="${active}"]`);
    if (!el || !track || track.scrollWidth <= track.clientWidth) return;
    track.scrollTo({ left: el.offsetLeft - track.clientWidth / 2 + el.offsetWidth / 2, behavior: 'smooth' });
  }, [active]);

  return (
    <nav
      aria-label="Workflow"
      className="hiw-rail"
      style={{ borderTop: `1px solid ${T.BORDER}`, borderBottom: `1px solid ${T.BORDER}`, backgroundColor: T.BG }}
    >
      <div ref={trackRef} className="hiw-rail-track">
        <div className={`${CONTAINER} flex items-center gap-1 py-3`}>
          {HOW_IT_WORKS_CHAPTERS.map((c, i) => {
            const on = c.slug === active;
            return (
              <span key={c.slug} className="flex items-center">
                {i > 0 && (
                  <span aria-hidden className="px-2.5 text-[12px]" style={{ color: T.BORDER }}>
                    —
                  </span>
                )}
                <a
                  href={`#${c.slug}`}
                  data-slug={c.slug}
                  aria-current={on ? 'true' : undefined}
                  className="flex items-center gap-2 px-1 py-1 text-[13.5px] no-underline"
                  style={{ color: on ? T.TEXT : T.TEXT_SOFT, fontWeight: on ? 700 : 500 }}
                >
                  <span
                    aria-hidden
                    className="h-[5px] w-[5px] rounded-full"
                    style={{ backgroundColor: on ? T.PRIMARY : 'transparent' }}
                  />
                  <span className="text-[11.5px] font-bold" style={{ fontFamily: MONO, color: on ? T.PRIMARY : T.TEXT_SOFT }}>
                    {c.n}
                  </span>
                  {c.short}
                </a>
              </span>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

export default function HowItWorksClient() {
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: HIW_CSS }} />

      {/* ─────────────────────────── Hero ─────────────────────────── */}
      <section className={`${CONTAINER} pb-16 pt-14 md:pb-24 md:pt-24`}>
        <div className="flex flex-col items-center gap-6 text-center">
          <MarketingPill icon={<Clapperboard />} label="How it works" />
          <h1
            className="max-w-[980px] text-[38px] sm:text-[48px] md:text-[64px] lg:text-[72px]"
            style={{ fontFamily: SERIF, fontWeight: 600, letterSpacing: '-0.033em', lineHeight: 1.03, color: T.TEXT }}
          >
            See exactly how a project runs{' '}
            <span className="italic" style={{ color: T.PRIMARY, fontWeight: 500 }}>in BuilderSiteNow.</span>
          </h1>
          <p className="max-w-[660px] text-[17px] leading-[1.7] md:text-[19px]" style={{ color: T.TEXT_MUTED }}>
            From the first enquiry to what the plot actually earned — recorded on the real screens.
          </p>
          <div className="flex w-full flex-col items-stretch gap-3 pt-2 sm:w-auto sm:flex-row sm:items-center">
            <Link
              href={demoHref('how-it-works')}
              className="hiw-cta inline-flex items-center justify-center gap-2 rounded-md px-7 py-3.5 text-[15.5px] font-semibold no-underline"
              style={{ color: T.ON_PRIMARY }}
            >
              Book a demo
              <ArrowRight className="l-arrow h-[17px] w-[17px]" />
            </Link>
            <Link
              href="/modules"
              className="inline-flex items-center justify-center gap-2 rounded-md px-7 py-3.5 text-[15.5px] font-semibold no-underline"
              style={{ color: T.TEXT, border: `1px solid ${T.BORDER}`, backgroundColor: T.SURFACE }}
            >
              Explore modules
            </Link>
          </div>
        </div>
      </section>

      <WorkflowRail />

      {HOW_IT_WORKS_CHAPTERS.map((c) => (
        <ChapterSection key={c.slug} c={c} />
      ))}

      {/* ─────────────────────────── Close ─────────────────────────── */}
      <section className={SECTION_PY} style={{ backgroundColor: T.PRIMARY_DARK, color: '#FFFFFF' }}>
        <div className={`${CONTAINER} max-w-[860px] text-center`}>
          <h2
            className="text-[34px] leading-[1.08] md:text-[52px]"
            style={{ fontFamily: SERIF, fontWeight: 600, letterSpacing: '-0.03em', color: '#FFFFFF' }}
          >
            Ready to run your next project better?
          </h2>
          <p className="mx-auto mt-5 max-w-[520px] text-[17px] leading-[1.7] md:text-[18.5px]" style={{ color: 'rgba(255,255,255,0.78)' }}>
            See BuilderSiteNow on one of your own sites, with your own data.
          </p>
          <div className="mt-10 flex flex-col items-center gap-7">
            <Link
              href={demoHref('how-it-works-closing')}
              className="inline-flex items-center justify-center gap-2 rounded-md px-9 py-4 text-[16px] font-semibold no-underline"
              style={{ backgroundColor: '#FFFFFF', color: T.PRIMARY_DARK }}
            >
              Book a demo
              <ArrowRight className="h-[18px] w-[18px]" />
            </Link>
            <a
              href={YOUTUBE_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[15px] font-semibold no-underline"
              style={{ color: 'rgba(255,255,255,0.86)', borderBottom: '1px solid rgba(255,255,255,0.32)' }}
            >
              Watch more walkthroughs
              <ArrowRight className="h-[15px] w-[15px]" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
